import {
  PromotionRepository,
  CreatePromotionInput,
  UpdatePromotionInput,
} from '../../domain/repositories/promotion.repository';
import { PromotionEntity, PromotionProps } from '../../domain/entities/promotion.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpPromotionRepository implements PromotionRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  public async getPromotions(options?: {
    activeOnly?: boolean;
    currentlyValid?: boolean;
    search?: string;
    productId?: string;
  }): Promise<PromotionEntity[]> {
    const params = new URLSearchParams();

    if (options?.activeOnly !== undefined) {
      params.append('activeOnly', String(options.activeOnly));
    }
    if (options?.currentlyValid !== undefined) {
      params.append('currentlyValid', String(options.currentlyValid));
    }
    if (options?.search?.trim()) {
      params.append('search', options.search.trim());
    }
    if (options?.productId?.trim()) {
      params.append('productId', options.productId.trim());
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await this.httpClient.get<ApiResponse<PromotionProps[]>>(`/promotions${query}`);

    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map((item) => new PromotionEntity(item));
  }

  public async getPublicActive(): Promise<PromotionEntity[]> {
    const res = await this.httpClient.get<ApiResponse<PromotionProps[]>>('/promotions/public');
    if (!res.success || !res.data) {
      return [];
    }
    return res.data.map((item) => new PromotionEntity(item));
  }

  public async getPromotionById(id: string): Promise<PromotionEntity | null> {
    const res = await this.httpClient.get<ApiResponse<PromotionProps>>(
      `/promotions/${encodeURIComponent(id)}`
    );
    if (!res.success || !res.data) {
      return null;
    }
    return new PromotionEntity(res.data);
  }

  public async createPromotion(data: CreatePromotionInput): Promise<PromotionEntity> {
    const res = await this.httpClient.post<ApiResponse<PromotionProps>>('/promotions', data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al crear la promoción');
    }
    return new PromotionEntity(res.data);
  }

  public async updatePromotion(id: string, data: UpdatePromotionInput): Promise<PromotionEntity> {
    const res = await this.httpClient.put<ApiResponse<PromotionProps>>(
      `/promotions/${encodeURIComponent(id)}`,
      data
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar la promoción');
    }
    return new PromotionEntity(res.data);
  }

  public async togglePromotionStatus(id: string, status: boolean): Promise<PromotionEntity> {
    const res = await this.httpClient.patch<ApiResponse<PromotionProps>>(
      `/promotions/${encodeURIComponent(id)}/status`,
      { status }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al alternar estado de la promoción');
    }
    return new PromotionEntity(res.data);
  }

  public async deletePromotion(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<null>>(
      `/promotions/${encodeURIComponent(id)}`
    );
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar la promoción');
    }
  }
}
